import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

type StepSnapshotBase = {
    key: string;
    velocity: number;
    basis: string | null;
    posBefore: string;
    tgtBefore: string;
};

type StepSnapshot = StepSnapshotBase & {
    posAfter: string;
    tgtAfter: string;
    forward: string;
    right: string;
    up: string;
};

type KeyCode = KeyboardEvent['code'];

export class CameraWasd {
    private static readonly EXPONENTIAL_SPEED_BASE = Math.SQRT2;
    private static readonly DISTANCE_SPEED_FACTOR = 0.2;
    private static readonly MIN_DISTANCE_REFERENCE = 2;
    private static readonly MAX_DISTANCE_REFERENCE = 20000;

    // one-shot breaker, last known good state, last writer
    private _lastGoodPos: THREE.Vector3 | null = null;
    private _lastGoodTgt: THREE.Vector3 | null = null;
    public _lastStep: StepSnapshot | null = null;

    private controls: OrbitControls;

    constructor(controls: OrbitControls) {
        this.controls = controls;
    }

    public update(deltaTime: number, activeKeys: Set<KeyCode>, speedLevel: number, boostMultiplier = 1): void {
        const controls = this.controls;

        if (!controls || activeKeys.size === 0) {
            return;
        }

        const dt = Number(deltaTime);
        const cam = controls.object;
        const target = controls.target;

        if (!this._isFiniteVec3(cam.position) || !this._isFiniteVec3(target)) {
            if (this._lastGoodPos && this._lastGoodTgt) {
                cam.position.copy(this._lastGoodPos);
                controls.target.copy(this._lastGoodTgt);
                controls.update();
            } else {
                return;
            }
        }

        const velocity = this.getVelocity(cam.position, target, speedLevel, boostMultiplier, dt);
        if (!Number.isFinite(velocity) || velocity === 0) {
            return;
        }

        if (!this._lastGoodPos) {
            this._lastGoodPos = cam.position.clone();
        }
        if (!this._lastGoodTgt) {
            this._lastGoodTgt = target.clone();
        }

        const forward = new THREE.Vector3();
        cam.getWorldDirection(forward);
        const f2 = this._len2(forward);
        if (!this._isFiniteVec3(forward) || f2 === 0) {
            return;
        }
        forward.multiplyScalar(1 / Math.sqrt(f2));

        const up = cam.up ? cam.up.clone() : new THREE.Vector3(0, 1, 0);
        const u2 = this._len2(up);
        if (!this._isFiniteVec3(up) || u2 === 0) {
            return;
        }
        up.multiplyScalar(1 / Math.sqrt(u2));

        const right = new THREE.Vector3().crossVectors(forward, up);
        const r2 = this._len2(right);
        if (!Number.isFinite(r2) || r2 === 0) {
            console.info('[WASD] right is zero (forward ~ up)', {
                forward: this._v(forward),
                up: this._v(up),
                dot: forward.dot(up).toFixed(8),
                keys: Array.from(activeKeys.values()),
            });
        } else {
            right.multiplyScalar(1 / Math.sqrt(r2));
        }

        let moved = false;

        const tryStep = (key: KeyCode, vec: THREE.Vector3 | null, scale: number): boolean => {
            const info: StepSnapshotBase = {
                key,
                velocity: scale,
                basis: vec ? this._v(vec) : null,
                posBefore: this._v(cam.position),
                tgtBefore: this._v(target),
            };

            if (vec) {
                cam.position.addScaledVector(vec, scale);
                target.addScaledVector(vec, scale);
            } else {
                cam.position.y += scale;
                target.y += scale;
            }

            moved = true;
            const bad = !this._isFiniteVec3(cam.position) || !this._isFiniteVec3(target);
            if (bad) {
                this._panic('WASD step produced non-finite', {
                    ...info,
                    posAfter: this._v(cam.position),
                    tgtAfter: this._v(target),
                    forward: this._v(forward),
                    right: this._v(right),
                    up: this._v(up),
                });
                return false;
            }

            this._lastStep = {
                ...info,
                posAfter: this._v(cam.position),
                tgtAfter: this._v(target),
                forward: this._v(forward),
                right: this._v(right),
                up: this._v(up),
            };

            if (this._lastGoodPos) {
                this._lastGoodPos.copy(cam.position);
            } else {
                this._lastGoodPos = cam.position.clone();
            }
            if (this._lastGoodTgt) {
                this._lastGoodTgt.copy(target);
            } else {
                this._lastGoodTgt = target.clone();
            }

            return true;
        };

        if ((activeKeys.has('KeyW') || activeKeys.has('ArrowUp')) && !tryStep('KeyW', forward, velocity)) return;
        if ((activeKeys.has('KeyS') || activeKeys.has('ArrowDown')) && !tryStep('KeyS', forward, -velocity)) return;
        if ((activeKeys.has('KeyA') || activeKeys.has('ArrowLeft')) && !tryStep('KeyA', right, -velocity)) return;
        if ((activeKeys.has('KeyD') || activeKeys.has('ArrowRight')) && !tryStep('KeyD', right, velocity)) return;
        if (activeKeys.has('KeyQ') && !tryStep('KeyQ', null, velocity)) return;
        if (activeKeys.has('KeyE') && !tryStep('KeyE', null, -velocity)) return;

        if (!moved) {
            return;
        }

        controls.update();
    }

    private getVelocity(
        position: THREE.Vector3,
        target: THREE.Vector3,
        speedLevel: number,
        boostMultiplier: number,
        deltaTime: number
    ): number {
        if (!Number.isFinite(deltaTime) || deltaTime <= 0) {
            return 0;
        }

        const distanceToTarget = THREE.MathUtils.clamp(
            position.distanceTo(target),
            CameraWasd.MIN_DISTANCE_REFERENCE,
            CameraWasd.MAX_DISTANCE_REFERENCE
        );
        const sanitizedSpeedLevel = Number.isFinite(speedLevel) ? Math.max(1, speedLevel) : 1;
        const exponentialSpeed = Math.pow(CameraWasd.EXPONENTIAL_SPEED_BASE, sanitizedSpeedLevel - 1);
        const sanitizedBoost = Number.isFinite(boostMultiplier) ? Math.max(1, boostMultiplier) : 1;

        return distanceToTarget * CameraWasd.DISTANCE_SPEED_FACTOR * exponentialSpeed * sanitizedBoost * deltaTime;
    }

    private _panic(message: string, payload: StepSnapshot): void {
        console.error(`[WASD] ${message}`, payload);
    }

    private _isFiniteVec3(v: THREE.Vector3): boolean {
        return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
    }

    private _len2(v: THREE.Vector3): number {
        return v.x * v.x + v.y * v.y + v.z * v.z;
    }

    private _v(v: THREE.Vector3): string {
        return `(${v.x.toFixed(5)}, ${v.y.toFixed(5)}, ${v.z.toFixed(5)})`;
    }
}
